import React, { FC, useContext, useRef, useState } from 'react';
import { Modal, Rows } from 'esa-ui-library';
import { CSVLink } from 'react-csv';
import { PropertyType } from 'esa-ui-library/lib/types';
import { find } from 'lodash';
import styles from './ActionExport.module.scss';
import { CSVExportType, ExportData, flattenAll, useStore } from '../../../../../common';
import { AuthContext } from '../../../../../logic';

interface ActionExportProps {
	data: CSVExportType;
}

const ActionExport: FC<ActionExportProps> = ({ data }): JSX.Element => {
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [exportData, setExportData] = useState<ExportData>({
		data: [],
		total: 0,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [exportLimit, setExportLimit] = useState<number>(1000);

	React.useEffect(() => {
		setExportLimit(1000);
	}, [data.filtersQuery]);

	const csvInstance = useRef<HTMLDivElement | null>(null);
	const { token, user } = useContext(AuthContext);
	const [state] = useStore();
	const { AAMS_types } = state || {};
	const isItaly = process.env.REACT_APP_SITE_TITLE?.includes('Italy');

	const getData = () => {
		if (data.api) {
			setLoading(true);
			const separator = data.filtersQuery.includes('?') ? '&' : '?';
			const queryWithLimit = `${data.filtersQuery}${separator}limit=${exportLimit}`;

			const apiData = {
				token,
				query: queryWithLimit,
			};
			data
				.api(apiData)
				.then((response: PropertyType) => {
					const formatData: ExportData = { data: [], total: 0 };
					const partners: PropertyType[] = user?.partners ? flattenAll(user.partners) : [];

					Object.keys(response).map((key) => {
						if (key === 'pagesTotal') {
							formatData.total = response[key] || 1;
						} else {
							const mappedData = response[key].map((player: PropertyType) => {
								const partnerFromConfig = partners.find((p: PropertyType) => p.value === player.group_id)?.label;

								const skin = partners.find((p: PropertyType) => p.skinId === player.skin_id);
								const partnerForSkin = skin?.partnerId
									? partners.find((p: PropertyType) => p.partnerId === skin.partnerId)
									: undefined;
								const partnerSkin =
									partnerForSkin?.label || skin?.label
										? `${partnerForSkin?.label ?? ''}${partnerForSkin?.label && skin?.label ? ' - ' : ''}${
												skin?.label ?? ''
										  }`
										: undefined;

								const gameId = player.game_id ?? player.internal_game_id;
								const providerName = player.provider_name ?? player.game_provider_name;
								const aamsTypeLabel =
									isItaly && AAMS_types
										? find(AAMS_types, (type: PropertyType) => `${type.value}` === `${player.aams_type_id}`)?.label
										: undefined;
								const aamsGameType = isItaly ? aamsTypeLabel || player.aams_game_id_type : player.aams_game_id_type;

								return {
									...player,
									...(gameId ? { game_id: gameId } : {}),
									...(providerName ? { provider_name: providerName } : {}),
									...(aamsGameType ? { aams_game_id_type: aamsGameType } : {}),
									partner: partnerFromConfig ?? player.partner,
									partner_skin: partnerSkin ?? player.partner_skin,
								};
							});
							formatData.data = mappedData;
						}
					});

					setExportData(formatData);
					setLoading(false);
					csvInstance.current?.click();
					if (formatData.total > exportLimit) {
						setExportLimit(exportLimit * 10);
						setTimeout(() => {
							setOpenModal(true);
						}, 300);
					}
				})
				.catch(() => {
					setLoading(false);
				});
		} else if (data.data) {
			setExportData(data.data);
			setLoading(false);
			setTimeout(() => {
				csvInstance.current?.click();
			}, 1000);
		}
	};

	const onCloseModal = () => setOpenModal(false);

	return (
		<div>
			<button className={styles.Button} data-testid='action-export' onClick={() => getData()}>
				{loading ? (
					<>
						Loading CSV...
						<Rows />
					</>
				) : (
					<>
						Export to CSV
						<Rows />
					</>
				)}
			</button>
			<CSVLink data={exportData.data} filename='report.csv' headers={data.csvHeaders} className={styles.ButtonCSV}>
				<div ref={csvInstance} />
			</CSVLink>
			{openModal && (
				<Modal
					primary
					confirm
					title='Too many records'
					submitLabel='Got it'
					onCancel={onCloseModal}
					onSubmit={onCloseModal}
				>
					<p>
						Data table contains more than {exportLimit / 10} entries, only {exportLimit / 10} rows will be exported to
						CSV file. Next, the export limit will be increased to {exportLimit}.
					</p>
				</Modal>
			)}
		</div>
	);
};

export { ActionExport };
